import * as React from 'react'
import { useState } from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableFooter from '@mui/material/TableFooter'
import { Link } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown'

import LoadingDefault from '../../../Components/Elements/Loading/LoadingDefault'
import ItemCounter from '../../../Components/Elements/ItemCounter'
import Pagination from '../../../Components/Elements/Pagination'
import DisplayItemHelper from '../../../util/DisplayItemHelper'
import ActionButtons from './Components/ActionButtons'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'
import { BadgeTag, CountDot } from '@/Components/Elements/StyledComponents/Tags'
import {
    aggregateTableCellSx,
    aggregateTableRowSx,
} from '@/Components/Elements/StyledComponents/styles'
import {
    formatDateTimeValue,
    getDateTimeLocale,
} from '../../DisplayItems/DateTimeField/formatDateTimeValue'
import {
    formatAmountValue,
    getAmountLocale,
} from '../../DisplayItems/formatAmountValue'

interface ContentDossierIndexFooterProps {
    listItems: Array<IDisplayField>
    dossiers: Array<any>
    kedo: any
    onChangePage: any
    loadingContent: boolean | null
    pager: any
    aggregations: any
    resultAggregations: any
    setAggregations: any
    extraColumns: any
    selected: Array<any>
    subscriptions: Array<any>
    defDossierId: number
    props: any
    mode: string
    limit: any
    handleEntriesChange: any
    onDeleteSuccess: any
    getSearchFilter: any
    fetchDossierResults: any
    favorites: Array<any>
    contents: any
    showArchived: boolean
}

const ContentDossierIndexFooter: React.FC<ContentDossierIndexFooterProps> = ({
    listItems,
    dossiers,
    kedo,
    onChangePage,
    loadingContent,
    pager,
    aggregations,
    setAggregations,
    resultAggregations,
    extraColumns,
    selected,
    subscriptions,
    defDossierId,
    props,
    mode,
    limit,
    handleEntriesChange,
    onDeleteSuccess,
    getSearchFilter,
    fetchDossierResults,
    favorites,
    contents,
    showArchived,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [activeField, setActiveField] = useState<number | null>(null)

    const selectedDossiers = dossiers.filter((item) =>
        selected.find((selectedId) => selectedId === item.id)
    )
    const archivedDossiers =
        showArchived && selectedDossiers.find((item) => item.archived === true)
    const dearchivedDossiers =
        showArchived && selectedDossiers.find((item) => item.archived === false)

    const hasSubscriptions = subscriptions && subscriptions.length > 0
    const totalColumns = listItems.length + extraColumns + 2 // +2 because of the empty cells at the start

    const getAggregationOptions = (field: IDisplayField) => {
        if (field.def_dossier_link) return ['unique']

        const type = field.def_dossier_def_field?.def_field?.type

        if (['amount', 'time'].includes(type))
            return ['avg', 'sum', 'min', 'max']
        if (type === 'date') return ['min', 'max']
        if (type === 'list') return ['unique']

        return []
    }

    const renderAggregateField = (field: IDisplayField) => {
        const aggrOptions = getAggregationOptions(field)

        if (!aggrOptions.length) return null

        return (
            <StyledButton
                className={'aggregate'}
                dense
                sx={{ border: 'none' }}
                size="small"
                onClick={(e) => {
                    setAnchorEl(e.currentTarget)
                    setActiveField(field)
                }}
            >
                <FontAwesomeIcon icon={faChevronDown} />
            </StyledButton>
        )
    }

    const renderAggregateFieldResult = (listItem) => {
        if (
            !resultAggregations ||
            resultAggregations[listItem.id] === null ||
            resultAggregations[listItem.id] === undefined
        ) {
            return null
        }

        if (listItem && listItem.def_dossier_link) {
            return (
                <div className={'aggr-result'}>
                    {resultAggregations[listItem.id].map(
                        (aggrItem, aggrIndex) => (
                            <Link
                                key={aggrIndex}
                                to={`/contentdossier/${aggrItem.id}`}
                            >
                                {aggrItem.summary}{' '}
                                <CountDot>{aggrItem.total}</CountDot>
                            </Link>
                        )
                    )}
                </div>
            )
        } else if (listItem && listItem.def_dossier_def_field) {
            return (
                <div className={'aggr-result'}>
                    {DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_DATE_TYPE
                    )
                        ? formatDateTimeValue(
                              resultAggregations[listItem.id],
                              getDateTimeLocale(kedo),
                              Boolean(
                                  listItem.def_dossier_def_field.def_field
                                      .settings?.withtime
                              )
                          )
                        : null}
                    {DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_TIME_TYPE
                    )
                        ? resultAggregations[listItem.id]
                        : null}
                    {DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_AMOUNT_TYPE
                    )
                        ? formatAmountValue(
                              resultAggregations[listItem.id],
                              listItem.def_dossier_def_field.def_field.settings,
                              getAmountLocale(kedo)
                          )
                        : null}
                    {DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_LIST_TYPE
                    )
                        ? listItem.def_dossier_def_field.def_field.def_field_lists
                              .filter((defFieldListItem) =>
                                  resultAggregations[listItem.id].find(
                                      (selItem) =>
                                          selItem.id === defFieldListItem.id
                                  )
                              )
                              .map((defFieldListItem, dIndex) => {
                                  const total =
                                      resultAggregations[listItem.id].find(
                                          (selItem) =>
                                              selItem.id === defFieldListItem.id
                                      )?.total ?? 0

                                  if (!total) return null

                                  const label = kedo.translateItem(
                                      defFieldListItem,
                                      'def_field_list'
                                  )

                                  return (
                                      <BadgeTag
                                          key={dIndex}
                                          color={
                                              defFieldListItem.color ||
                                              undefined
                                          }
                                          textColor={
                                              defFieldListItem.color
                                                  ? '#fff'
                                                  : undefined
                                          }
                                          size="small"
                                      >
                                          {label}
                                          <CountDot inverted>{total}</CountDot>
                                      </BadgeTag>
                                  )
                              })
                        : null}
                </div>
            )
        }
    }

    const showAggregations =
        kedo.isAdminOrEnvironmentAdmin() &&
        listItems.some(
            (listItem) =>
                getAggregationOptions(listItem).length > 0 ||
                resultAggregations?.[listItem.id]
        )

    if (loadingContent) {
        return (
            <TableFooter>
                <TableRow
                    sx={(theme) => ({
                        '& .MuiTableCell-root': {
                            padding: `${theme.spacing(0.5)} ${theme.spacing(
                                1
                            )}`,
                        },
                    })}
                >
                    {' '}
                    <TableCell
                        colSpan={totalColumns}
                        sx={{ textAlign: 'center' }}
                    >
                        <LoadingDefault />
                    </TableCell>
                </TableRow>
            </TableFooter>
        )
    }

    if (!dossiers || dossiers.length <= 0) {
        return <tfoot />
    }

    const isAllowedDd = (credential: string): boolean => {
        return kedo
            .env()
            .isAllowedDefDossier(credential, defDossierId, kedo.user())
    }

    return (
        <>
            <TableFooter>
                {showAggregations && (
                    <TableRow sx={aggregateTableRowSx} className={'tr-aggr'}>
                        <TableCell sx={{ width: '3%' }} />
                        <TableCell align="center" />
                        {listItems.map((listItem, lIndex) => (
                            <TableCell key={lIndex} sx={aggregateTableCellSx}>
                                {renderAggregateField(listItem)}
                                {renderAggregateFieldResult(listItem)}
                            </TableCell>
                        ))}
                        {Array.from({ length: extraColumns - 1 }).map(
                            (_, i) => (
                                <TableCell key={i} />
                            )
                        )}
                    </TableRow>
                )}
                {selected.length > 0 && (
                    <TableRow
                        sx={{
                            '&& .MuiTableCell-root': {
                                borderTop: 1,
                                borderTopColor: 'divider',
                                py: 1,
                            },
                        }}
                    >
                        <TableCell colSpan={totalColumns}>
                            <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent={
                                    selected.length
                                        ? 'space-between'
                                        : 'flex-start'
                                }
                                alignItems="center"
                            >
                                <ActionButtons
                                    selected={selected}
                                    selectedDossiers={selectedDossiers}
                                    isAllowedDd={isAllowedDd}
                                    hasSubscriptions={hasSubscriptions}
                                    dearchivedDossiers={dearchivedDossiers}
                                    archivedDossiers={archivedDossiers}
                                    kedo={kedo}
                                    props={props}
                                    mode={mode}
                                    dossiers={dossiers}
                                    pager={pager}
                                    limit={limit}
                                    handleEntriesChange={handleEntriesChange}
                                    onDeleteSuccess={onDeleteSuccess}
                                    defDossierId={defDossierId}
                                    getSearchFilter={getSearchFilter}
                                    fetchDossierResults={fetchDossierResults}
                                    subscriptions={subscriptions}
                                    favorites={favorites}
                                    contents={contents}
                                />
                            </Stack>
                        </TableCell>
                    </TableRow>
                )}
                <TableRow
                    sx={{
                        '&& .MuiTableCell-root': {
                            borderTop: 1,
                            borderTopColor: 'divider',
                            py: 1,
                        },
                    }}
                >
                    <TableCell colSpan={totalColumns}>
                        <Pagination pager={pager} onChangePage={onChangePage} />
                        <ItemCounter pager={pager} />
                    </TableCell>
                </TableRow>
            </TableFooter>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => {
                    setAnchorEl(null)
                    setActiveField(null)
                }}
            >
                <MenuItem
                    onClick={() => {
                        const aggrs = aggregations.filter(
                            (item) => item.id !== activeField?.id
                        )
                        setAggregations(aggrs)
                        setAnchorEl(null)
                    }}
                >
                    —
                </MenuItem>

                {activeField &&
                    getAggregationOptions(activeField).map((option) => (
                        <MenuItem
                            key={option}
                            selected={
                                aggregations.find(
                                    (a) => a.id === activeField.id
                                )?.aggregate === option
                            }
                            onClick={() => {
                                const aggrs = aggregations.filter(
                                    (item) => item.id !== activeField.id
                                )

                                aggrs.push({
                                    id: activeField.id,
                                    aggregate: option,
                                })

                                setAggregations(aggrs)
                                setAnchorEl(null)
                            }}
                        >
                            {kedo.t(option.toUpperCase())}
                        </MenuItem>
                    ))}
            </Menu>
        </>
    )
}

export default ContentDossierIndexFooter
